import { z } from 'zod';

/**
 * F34 — trainer income reports. GROSS only at MVP (sum of booking price_amount
 * where payment_status='paid', rolled up in `trainer_income_daily`). The
 * net / commission / invoice breakdown is F35 — the income screen shows a
 * clearly-labelled "Coming with F35" stub for it.
 */

export const TRAINER_INCOME_PERIOD_VALUES = ['week', 'month', 'quarter'] as const;
export type TrainerIncomePeriod = (typeof TRAINER_INCOME_PERIOD_VALUES)[number];
export const TrainerIncomePeriodSchema = z.enum(TRAINER_INCOME_PERIOD_VALUES);

export const TrainerIncomeQuerySchema = z
  .object({
    period: TrainerIncomePeriodSchema.default('month'),
    /** Optional explicit window override (ISO date). Defaults derive from `period`. */
    from: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    to: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
  })
  .strict();
export type TrainerIncomeQuery = z.infer<typeof TrainerIncomeQuerySchema>;

/** Line-chart point (gross over time). */
export type TrainerIncomePoint = {
  readonly date: string;
  readonly grossAmount: number;
  readonly sessionCount: number;
};

/** Bar-chart bucket (gross by sport). */
export type TrainerIncomeBySport = {
  readonly sportName: string;
  readonly grossAmount: number;
  readonly sessionCount: number;
};

/** Donut slice (gross by package). `packageId` NULL = pay-per-session bookings. */
export type TrainerIncomeByPackage = {
  readonly packageId: string | null;
  readonly packageName: string | null;
  readonly grossAmount: number;
  readonly sessionCount: number;
};

export type TrainerIncomeReport = {
  readonly period: TrainerIncomePeriod;
  readonly from: string;
  readonly to: string;
  readonly currency: string;
  readonly totalGross: number;
  readonly sessionCount: number;
  readonly series: {
    readonly line: readonly TrainerIncomePoint[];
    readonly bySport: readonly TrainerIncomeBySport[];
    readonly byPackage: readonly TrainerIncomeByPackage[];
  };
};
